import { chunk } from "lodash";
import { LoadingDots } from "PFComponents/loading_dots";
import roundToDecimals from "PFCore/helpers/round_to_decimals";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { SupplyTotalStats } from "PFCore/services/supply_reports";
import { FeatureFlag } from "PFTypes";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatNumber } from "../../../../../utils/utils";
import BoxRenderer, { BoxItem } from "../../../../common/box_renderer";
import TilePreview from "../../../../previews/tile_preview";
import { VisibleCategory } from "../../../../types/types";
import { Utilization } from "../../supply_section";

type Data = Omit<SupplyTotalStats, "categories"> & {
  categories: VisibleCategory[];
};

type TilesSectionProps = {
  data: Data;
  utilizationOption: Utilization;
  loading: boolean;
  error: boolean;
};

const TilesSection = ({ data, utilizationOption, loading, error }: TilesSectionProps): JSX.Element => {
  const { t } = useTranslation("insights");
  const showUtilizationMetrics = useIsFeatureEnabled()(FeatureFlag.ShowUtilization);

  const { billable_utilisation, total_utilisation, availability, capacity, categories } = data;

  const categoriesToRender = useMemo(
    () =>
      categories
        ? chunk(
            categories?.filter((item) => item.visible),
            3
          )
        : [],
    [categories]
  );

  const getUtilizationMetrics = (): BoxItem[] => {
    if (!showUtilizationMetrics) {
      return [];
    }
    const utilization = utilizationOption === Utilization.Total ? total_utilisation : billable_utilisation;
    return [
      {
        title: t(`bookingReports.${utilizationOption}Utilization`),
        value: `${formatNumber(roundToDecimals(utilization * 100))}%`,
        big: true
      }
    ];
  };

  const boxItems: BoxItem[][] = useMemo(
    () =>
      categoriesToRender.map((row) =>
        row.map((item: VisibleCategory) => ({
          title: [item.category],
          value: `${formatNumber(Math.round(item.value as number))}h`
        }))
      ),
    [categoriesToRender]
  );

  if (loading) {
    return <LoadingDots circlesEnabled pageViewEnabled />;
  }

  if (!data || error) {
    return <TilePreview loading content={t("noData")} />;
  }

  return (
    <div>
      <BoxRenderer
        boxItem={[
          ...getUtilizationMetrics(),
          {
            title: t("bookingReports.availability"),
            value: `${formatNumber(availability?.toFixed(0))}h`,
            big: true
          },
          {
            title: t("bookingReports.capacity"),
            value: `${formatNumber(capacity?.toFixed(0))}h`,
            big: true
          }
        ]}
      />
      {boxItems.map((boxItem, index) => (
        <BoxRenderer boxItem={boxItem} key={index} />
      ))}
    </div>
  );
};

export default TilesSection;
